import { createTheme, alpha } from '@mui/material/styles'
import typography from './typography'

const sharedCard = {
  borderRadius: '1.6rem',
  variant: {
    small: {
      padding: '1.6rem',
      minHeight: '9.9rem',
    },
    track: {
      minHeight: '18.2rem',
      padding: '1.6rem',
    },
    gutterless: {
      padding: '0',
      borderRadius: '0.8rem',
    },
    default: {
      padding: '2rem',
    },
  },
  background: '#fff',
}

const sharedComponents = {
  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: '1.6rem',
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        variants: [
          {
            props: { size: 'medium' },
            style: {
              padding: '1.4rem 1.6rem',
              borderRadius: '0.8rem',
              fontSize: '1.4rem',
              lineHeight: '2rem',
            },
          },
          {
            props: { size: 'small' },
            style: {
              ...typography.buttonSm,
            },
          },
        ],
      },
    },
  },
}

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    text: {
      primary: '#26283D',
      secondary: alpha('#26283D', 0.5),
      contrast: '#fff',
      hover: '#9381EA',
      disabled: alpha('#26283D', 0.4),
    },
    primary: {
      main: '#6254A4',
      light: '#8171CE',
      dark: '#50428F',
      contrastText: '#fff',
    },
    secondary: {
      main: '#EFEFEF',
      dark: '#EAEAEA',
      dark2: '#E2E2E2',
    },
    background: {
      default: '#F6F5F3',
      secondary: alpha('#26283D', 0.2),
      tertiary: '#fff',
      paper: '#fff',
      tabs: '#F6F5F3',
      icon: alpha('#FFFFFF', 0.2),
    },
    buttonBorder: alpha('#26283D', 0.2),
    chartCaption: '#26283D',
    inputBackground: '#F7F8FC',
    chipBackground: '#E2E2E2',
    filterSearchBg: '#EAEAE9',
    tableRow: '#F6F5F3',
    track: {
      doctor:
        'linear-gradient(180deg, #FF9595 0%, #FA7070 100%), linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
      medicine:
        'linear-gradient(180deg, #FFBE72 0%, #FEA338 100%), linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
      leadership:
        'linear-gradient(180deg, #B09FFF 0%, #8D79F6 100%), linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
      it: 'linear-gradient(180deg, #76D8D8 0%, #02AFAF 100%), linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
      lab: 'linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), linear-gradient(180deg, #FF9595 0%, #FA7070 100%), linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
      solid: {
        doctor: '#FFAFAF',
        medicine: '#FFD590',
        leadership: '#C6CAFF',
        it: '#BEF0D9',
        lab: '#AAEEF3',
      },
    },
    status: {
      error: '#FFAFAF',
      warning: '#FFD590',
      success: '#BEF0D9',
    },
  },
  gradient: {
    green: 'linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
    coral: 'linear-gradient(180deg, #FF9595 0%, #FA7070 100%), #FA7070',
    blue: 'linear-gradient(180deg, #79ABF6 0%, #427FDB 100%), #FA7070',
  },
  chart: {
    blue: '#76D0FE',
    purple: '#AC9BFE',
    coral: '#FA7070',
    green: '#60C35B',
    yellow: '#FECF64',
    grey: '#F6F6F6',
  },
  spacing: (value) => `${value}rem`,
  typography,
  card: sharedCard,
  components: sharedComponents,
})

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: alpha('#fff', 0.5),
      contrast: '#fff',
      hover: '#8171ce',
      disabled: alpha('#fff', 0.4),
    },
    primary: {
      main: '#6E5BC8',
      light: '#9381EA',
      dark: '#9381EA',
      dark2: '#50428F',
      contrastText: '#fff',
    },
    secondary: {
      main: '#383B44',
      dark: '#464A55',
      dark2: '#363944',
    },
    background: {
      default: '#131517',
      secondary: '#383B44',
      tertiary: alpha('#26283D', 0.2),
      paper: '#1D1F24',
      tabs: '#2C2E34',
      icon: alpha('#26283D', 0.2),
    },
    buttonBorder: alpha('#fff', 0.2),
    chartCaption: '#26283D',
    inputBackground: '#F7F8FC',
    chipBackground: '#1D1F24',
    filterSearchBg: alpha('#26283D', 0.2),
    tableRow: '#2C2E34',
    track: {
      doctor: 'linear-gradient(180deg, #FF9595 0%, #FA7070 100%)',
      medicine: 'linear-gradient(180deg, #FFBE72 0%, #FEA338 100%)',
      leadership: 'linear-gradient(180deg, #B09FFF 0%, #8D79F6 100%)',
      it: 'linear-gradient(180deg, #76D8D8 0%, #02AFAF 100%)',
      lab: 'linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%)',
      solid: {
        doctor: '#FA7070',
        medicine: '#FEA338',
        leadership: '#8D79F6',
        it: '#14C08C',
        lab: '#0DB0B6',
      },
    },
    status: {
      error: '#FA7070',
      warning: '#FEA338',
      success: '#14C08C',
    },
  },
  gradient: {
    green: 'linear-gradient(180deg, #45C9A1 0%, #02AF7B 100%), #02AF7B',
    coral: 'linear-gradient(180deg, #FF9595 0%, #FA7070 100%), #FA7070',
    blue: 'linear-gradient(180deg, #79ABF6 0%, #427FDB 100%), #FA7070',
  },
  chart: {
    blue: '#76D0FE',
    purple: '#AC9BFE',
    coral: '#FA7070',
    green: '#60C35B',
    yellow: '#FECF64',
    grey: '#F6F6F6',
  },
  spacing: (value) => `${value}rem`,
  typography,
  card: sharedCard,
  components: {
    ...sharedComponents,
    MuiPaper: {
      root: {},
    },
  },
})

const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-toolpad-color-scheme' },
  colorSchemes: { light: lightTheme, dark: darkTheme },
})

export default theme
